/**
 * Assembling one Anthropic streamed turn from its events (18.08.2026).
 *
 * Streaming text is easy; streaming TOOL CALLS is where this earns its keep.
 * A tool call does not arrive whole. Anthropic opens a `content_block_start`
 * carrying the tool's id and name, then streams its arguments as a series of
 * `input_json_delta` fragments, then closes with `content_block_stop`. Only at
 * that last event is the JSON parseable.
 *
 * Emitting on any earlier event means executing a tool with truncated
 * arguments — `terminal_run_command` with half a command, `update_request`
 * with half a URL. That is worse than not streaming at all, which is why the
 * assembly lives in its own tested module rather than inline in the transport:
 * this is the part that must be right, and it is pure, so it can be proven
 * without a live key.
 *
 * The caller feeds each `data:` payload to `pushEvent`. Text deltas surface
 * immediately (that is the visible win); tool calls surface only when complete.
 */

/** A fresh accumulator for one turn. */
export function createTurnAssembly() {
  return {
    /** index → { id, name, json } for tool_use blocks still being streamed. */
    pending: new Map(),
    stopReason: null,
    usage: null,
  };
}

/**
 * Feed one SSE payload in; get back what the caller should emit now.
 *
 * Returns `{ text?, toolUse?, usage?, stopReason?, done? }` — every field
 * optional, and an event that means nothing to us returns an empty object
 * rather than null, so callers never have to null-check before destructuring.
 */
export function pushEvent(state, payload) {
  if (!payload || payload === '[DONE]') return {};

  let event;
  try {
    event = JSON.parse(payload);
  } catch {
    // A malformed frame must not kill a turn that is otherwise fine. The next
    // frame usually is; a truly broken stream ends with an error event anyway.
    return {};
  }

  switch (event?.type) {
    case 'message_start':
      // Input tokens are only reported here — the final message_delta carries
      // output tokens alone, so dropping this loses half the usage figure the
      // context meter is built on.
      if (event.message?.usage) state.usage = { ...event.message.usage };
      return {};

    case 'content_block_start': {
      const block = event.content_block;
      if (block?.type === 'tool_use') {
        state.pending.set(event.index, { id: block.id, name: block.name, json: '' });
      }
      // A text block's start carries no text worth emitting; its deltas do.
      return {};
    }

    case 'content_block_delta': {
      const delta = event.delta;
      if (delta?.type === 'text_delta' && delta.text) return { text: delta.text };
      if (delta?.type === 'input_json_delta') {
        const pendingBlock = state.pending.get(event.index);
        // Accumulated, NOT emitted. This is the whole point of the module.
        if (pendingBlock) pendingBlock.json += delta.partial_json ?? '';
      }
      return {};
    }

    case 'content_block_stop': {
      const pendingBlock = state.pending.get(event.index);
      if (!pendingBlock) return {};
      state.pending.delete(event.index);
      return { toolUse: finishToolUse(pendingBlock) };
    }

    case 'message_delta': {
      if (event.delta?.stop_reason) state.stopReason = event.delta.stop_reason;
      // Output tokens arrive here; merge rather than replace so the input
      // count from message_start survives.
      if (event.usage) state.usage = { ...(state.usage || {}), ...event.usage };
      return { usage: state.usage };
    }

    case 'message_stop':
      return { done: true, stopReason: state.stopReason || 'end_turn' };

    case 'error':
      // Anthropic can report a mid-stream error as an event rather than by
      // closing the connection. Surfacing it as a thrown-shaped result lets
      // the caller treat both the same way.
      return { error: event.error?.message || 'Stream error' };

    default:
      // ping, and anything added to the protocol later.
      return {};
  }
}

/**
 * Turn an accumulated tool block into the shape the panel dispatches.
 *
 * An empty argument string is `{}`, not a parse failure: a tool with no
 * required inputs (get_collections, get_boards) streams no input_json_delta at
 * all, so "" is the correct and common case.
 *
 * A non-empty string that does not parse means the stream was cut mid-call.
 * That is reported as an error rather than guessed at — a half-parsed input is
 * how a truncated command gets run.
 */
function finishToolUse(block) {
  const raw = block.json.trim();
  if (!raw) return { id: block.id, name: block.name, input: {} };
  try {
    return { id: block.id, name: block.name, input: JSON.parse(raw) };
  } catch {
    return {
      id: block.id,
      name: block.name,
      input: {},
      incomplete: true,
    };
  }
}

/**
 * Tool calls still open when the stream ended.
 *
 * A stream that dies mid-tool-call leaves entries here. They are deliberately
 * NOT emitted: an unfinished call has unfinished arguments, and running one is
 * the failure this module exists to prevent. The caller reports the turn as
 * incomplete instead.
 */
export function unfinishedToolCalls(state) {
  return [...state.pending.values()].map((b) => ({ id: b.id, name: b.name }));
}
